import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { backendURL } from "../config";
import {
    Sparkles, Bot, Shield, ArrowRight, CheckCircle2, Clock,
    Plus, ChevronRight, Check, AlertTriangle, ShieldCheck,
    Cpu, Globe, Terminal, Play, Zap, RefreshCw, X, Radio,
    FileCode, Sliders, ArrowUpRight, BarChart2, Search,
    Filter, Lock, Server, Users, ShieldAlert, Upload,
    BookOpen, Layers, GitBranch, Edit3, SlidersHorizontal, Trash2
} from "lucide-react";

export default function SkillLibrary() {
    const navigate = useNavigate();
    const [skills, setSkills] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState("custom"); // "custom" | "expansion"
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedModule, setSelectedModule] = useState("ALL");
    const [selectedSkill, setSelectedSkill] = useState(null);
    const [toast, setToast] = useState(null);
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const fileInputRef = useRef(null);
    const [newSkillForm, setNewSkillForm] = useState({
        name: "",
        type: "Strategy",
        module: "Investigation",
        description: "",
        prompt: ""
    });

    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };

    useEffect(() => {
        fetchSkills();
    }, []);

    const fetchSkills = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${backendURL}/api/skills`, { headers });
            setSkills(res.data.skills || []);
        } catch (err) {
            if (err.response?.status === 401) {
                localStorage.removeItem("token");
                window.location.href = "/login";
                return;
            }
            console.error("Failed to load skills:", err);
        }
        setLoading(false);
    };

    const handleToggleSkill = async (skill, e) => {
        e.stopPropagation();
        const nextState = !skill.enabled;
        try {
            await axios.post(`${backendURL}/api/skills/${skill.id}/toggle`, { enabled: nextState }, { headers });
            setSkills(prev => prev.map(s => s.id === skill.id ? { ...s, enabled: nextState, status: nextState ? "ACTIVE" : "INACTIVE" } : s));
            setToast(`Skill ${nextState ? "Activated" : "Deactivated"}`);
            setTimeout(() => setToast(null), 2500);
        } catch (err) {
            console.error("Failed to toggle skill:", err);
        }
    };

    const handleCreateSkill = async (e) => {
        e.preventDefault();
        try {
            const res = await axios.post(`${backendURL}/api/skills`, newSkillForm, { headers });
            if (res.data.success) {
                setSkills(prev => [res.data.skill, ...prev]);
                setCreateModalOpen(false);
                setNewSkillForm({
                    name: "",
                    type: "Strategy",
                    module: "Investigation",
                    description: "",
                    prompt: ""
                });
                setToast("New AI Skill Created & Activated!");
                setTimeout(() => setToast(null), 3000);
            }
        } catch (err) {
            console.error("Failed to create skill:", err);
            setToast("Failed to create skill: " + (err.response?.data?.error || err.message));
            setTimeout(() => setToast(null), 3500);
        }
    };

    const handleDeleteSkill = async (e, skillId, skillName) => {
        if (e) {
            e.stopPropagation();
            e.preventDefault();
        }
        const confirmed = window.confirm(`Are you sure you want to permanently delete the skill "${skillName || skillId}"?`);
        if (!confirmed) return;

        try {
            await axios.delete(`${backendURL}/api/skills/${skillId}`, { headers });
            setSkills(prev => prev.filter(s => s.id !== skillId));
            setToast(`Skill "${skillName || skillId}" deleted successfully`);
            setTimeout(() => setToast(null), 3000);
        } catch (err) {
            console.error("Failed to delete skill:", err);
            setToast("Failed to delete skill: " + (err.response?.data?.error || err.message));
            setTimeout(() => setToast(null), 3500);
        }
    };

    const handleFileUpload = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = async (event) => {
            try {
                const text = event.target.result;
                let payload = {};

                if (file.name.endsWith(".json")) {
                    const parsed = JSON.parse(text);
                    payload = {
                        name: parsed.name || parsed.title || file.name.replace(/\.[^/.]+$/, ""),
                        type: parsed.type || "Strategy",
                        module: parsed.module || "Investigation",
                        description: parsed.description || "Imported AI Strategy",
                        prompt: parsed.prompt || parsed.instructions || text
                    };
                } else {
                    // Markdown or YAML skill import
                    const lines = text.split("\n");
                    const name = lines[0]?.replace(/^#+\s*/, "")?.trim() || file.name.replace(/\.[^/.]+$/, "");
                    payload = {
                        name,
                        type: "Strategy",
                        module: "Investigation",
                        description: lines.slice(1, 4).join(" ").trim() || "Imported custom SOP playbook",
                        prompt: text
                    };
                }

                const res = await axios.post(`${backendURL}/api/skills`, payload, { headers });
                if (res.data.success) {
                    setSkills(prev => [res.data.skill, ...prev]);
                    setToast(`Imported "${payload.name}" successfully!`);
                    setTimeout(() => setToast(null), 3000);
                }
            } catch (err) {
                console.error("Failed to parse/upload skill file:", err);
                setToast("Error parsing file. Please provide valid JSON, YAML, or Markdown.");
                setTimeout(() => setToast(null), 3500);
            }
        };
        reader.readAsText(file);
        e.target.value = "";
    };

    const customSkills = skills.filter(s => s.category === "custom");
    const expansionSkills = skills.filter(s => s.category === "expansion");
    const displayedSkills = activeTab === "custom" ? customSkills : expansionSkills;

    const filteredSkills = displayedSkills.filter(s => {
        const matchesSearch =
            s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (s.description || "").toLowerCase().includes(searchQuery.toLowerCase());
        const matchesModule = selectedModule === "ALL" || s.module?.includes(selectedModule);
        return matchesSearch && matchesModule;
    });

    const getModuleColor = (module) => {
        if (module.includes("Investigation")) return "text-blue-400";
        if (module.includes("Threat Hunt")) return "text-amber-400";
        if (module.includes("Chat")) return "text-purple-400";
        return "text-slate-400";
    };

    return (
        <div className="w-full space-y-6 pb-12">
            {/* ─── Toast Feedback ─── */}
            {toast && (
                <div className="fixed bottom-6 right-6 z-50 bg-purple-600 text-white px-4 py-2.5 rounded-xl shadow-2xl text-[12px] font-bold flex items-center gap-2 animate-in fade-in slide-in-from-bottom-4 duration-200">
                    <Check className="w-4 h-4" />
                    {toast}
                </div>
            )}

            {/* ─── Breadcrumb ─── */}
            <div className="flex items-center gap-2 text-[11px] font-mono text-slate-500 uppercase tracking-wider">
                <span>01</span>
                <span>/</span>
                <span className="text-slate-400">A Skill Library</span>
            </div>

            {/* ─── Main Hero Layout (2 Columns) ─── */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                
                {/* ═══ LEFT HERO DESCRIPTION (4.5 cols) ═══ */}
                <div className="lg:col-span-4 space-y-6">
                    <div>
                        <h1 className="text-[28px] lg:text-[34px] font-black text-white tracking-tight leading-tight">
                            Every strategy, in one place.
                        </h1>
                        <p className="text-[13.5px] text-slate-300 mt-3 leading-relaxed">
                            Skills are reusable investigation and hunting strategies you can switch on and off. Some you write, some <strong>QueryTel generates from your environment</strong>, and each one is scoped to the modules and alerts where it belongs.
                        </p>
                    </div>

                    <div className="space-y-2.5 pt-2">
                        <div className="flex items-center gap-2.5 text-[13px] text-slate-200">
                            <div className="w-4 h-4 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center flex-shrink-0">
                                <Check className="w-3 h-3" />
                            </div>
                            <span>Skills for investigation, threat hunting, and chat.</span>
                        </div>
                        <div className="flex items-center gap-2.5 text-[13px] text-slate-200">
                            <div className="w-4 h-4 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center flex-shrink-0">
                                <Check className="w-3 h-3" />
                            </div>
                            <span>Toggle any skill on, off, or keep it as a draft.</span>
                        </div>
                        <div className="flex items-center gap-2.5 text-[13px] text-slate-200">
                            <div className="w-4 h-4 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center flex-shrink-0">
                                <Check className="w-3 h-3" />
                            </div>
                            <span>Written by your team, or generated by QueryTel.</span>
                        </div>
                    </div>

                    {/* Quick Stats Summary */}
                    <div className="bg-[#111827] border border-white/[0.06] rounded-2xl p-5 shadow-sm space-y-3">
                        <div className="flex items-center justify-between">
                            <span className="text-[12px] font-bold text-white uppercase tracking-wider">AI Skill Telemetry</span>
                            <span className="text-[10px] font-mono text-purple-400 bg-purple-500/10 px-2 py-0.5 rounded border border-purple-500/20">
                                QueryTel Engine
                            </span>
                        </div>
                        <div className="grid grid-cols-2 gap-3 text-[12px]">
                            <div className="p-3 bg-black/30 rounded-xl border border-white/[0.04]">
                                <span className="text-[10px] text-slate-500 font-mono uppercase block">Custom Skills</span>
                                <span className="text-[18px] font-bold text-white font-mono flex items-baseline gap-1.5">
                                    {customSkills.filter(s => s.enabled).length}
                                    <span className="text-[11px] text-slate-400 font-normal">Active ({customSkills.length} Total)</span>
                                </span>
                            </div>
                            <div className="p-3 bg-black/30 rounded-xl border border-white/[0.04]">
                                <span className="text-[10px] text-slate-500 font-mono uppercase block">Expansion Pack</span>
                                <span className="text-[18px] font-bold text-emerald-400 font-mono flex items-baseline gap-1.5">
                                    {expansionSkills.length}
                                    <span className="text-[11px] text-slate-400 font-normal">Built-in</span>
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ═══ RIGHT WORKBENCH: Skills Inventory Table (7.5 cols) ═══ */}
                <div className="lg:col-span-8 bg-[#111827] border border-white/[0.06] rounded-2xl p-6 shadow-2xl space-y-5">
                    
                    {/* Top Tabs matching screenshot 01 */}
                    <div className="flex items-center gap-6 border-b border-white/[0.06] pb-3 text-[13px] font-bold">
                        <button
                            onClick={() => setActiveTab("custom")}
                            className={`pb-2 transition-all relative ${
                                activeTab === "custom" ? "text-white" : "text-slate-500 hover:text-slate-300"
                            }`}
                        >
                            My Custom Skills ({customSkills.length})
                            {activeTab === "custom" && (
                                <div className="absolute bottom-[-13px] left-0 right-0 h-0.5 bg-purple-500 rounded-full" />
                            )}
                        </button>
                        <button
                            onClick={() => setActiveTab("expansion")}
                            className={`pb-2 transition-all relative ${
                                activeTab === "expansion" ? "text-white" : "text-slate-500 hover:text-slate-300"
                            }`}
                        >
                            QueryTel Expansion Skills ({expansionSkills.length})
                            {activeTab === "expansion" && (
                                <div className="absolute bottom-[-13px] left-0 right-0 h-0.5 bg-purple-500 rounded-full" />
                            )}
                        </button>
                    </div>

                    {/* Filter Controls Bar */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div className="flex items-center gap-2 flex-1">
                            {/* Search */}
                            <div className="flex items-center bg-black/40 border border-white/[0.08] rounded-lg px-3 py-1.5 flex-1 focus-within:border-purple-500/40">
                                <Search className="w-3.5 h-3.5 text-slate-500 mr-2" />
                                <input
                                    type="text"
                                    placeholder="Search skills..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="bg-transparent border-none outline-none text-[12px] text-white placeholder:text-slate-600 w-full"
                                />
                            </div>

                            {/* Module Filter */}
                            <select
                                value={selectedModule}
                                onChange={(e) => setSelectedModule(e.target.value)}
                                className="bg-[#1E293B] text-slate-200 border border-white/[0.12] text-[11px] font-medium rounded-lg px-2.5 py-1.5 outline-none"
                            >
                                <option value="ALL">All Modules</option>
                                <option value="Investigation">Investigation</option>
                                <option value="Threat Hunt">Threat Hunt</option>
                                <option value="Copilot Chat">Copilot Chat</option>
                            </select>
                        </div>

                        <div className="flex items-center gap-2">
                            <input
                                type="file"
                                ref={fileInputRef}
                                onChange={handleFileUpload}
                                accept=".json,.yaml,.yml,.md,.txt"
                                className="hidden"
                            />
                            <button
                                type="button"
                                onClick={() => fileInputRef.current?.click()}
                                className="px-3 py-1.5 bg-white/[0.04] hover:bg-white/[0.08] text-slate-300 rounded-lg text-[11.5px] font-medium flex items-center gap-1.5 transition-all"
                            >
                                <Upload className="w-3.5 h-3.5" />
                                Upload Skill
                            </button>
                            <button
                                onClick={() => setCreateModalOpen(true)}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-600 hover:bg-purple-500 text-white rounded-lg text-[11.5px] font-bold shadow-md transition-all whitespace-nowrap"
                            >
                                <Plus className="w-3.5 h-3.5" />
                                Create Skill
                            </button>
                        </div>
                    </div>

                    {/* Skills Table */}
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse text-[12px]">
                            <thead className="border-b border-white/[0.06] bg-black/40 text-[10.5px] uppercase font-mono text-slate-400">
                                <tr>
                                    <th className="py-3 px-4">Name</th>
                                    <th className="py-3 px-3">Type</th>
                                    <th className="py-3 px-3">Module</th>
                                    <th className="py-3 px-3">Status</th>
                                    <th className="py-3 px-3 text-right">Last updated</th>
                                    <th className="py-3 px-3 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-white/[0.04]">
                                {filteredSkills.map((sk) => (
                                    <tr
                                        key={sk.id}
                                        onClick={() => navigate(`/skills/${sk.id}`)}
                                        className="hover:bg-white/[0.02] cursor-pointer transition-colors group"
                                    >
                                        {/* Name */}
                                        <td className="py-3.5 px-4 font-bold text-white group-hover:text-purple-400 transition-colors whitespace-nowrap">
                                            <div className="flex items-center gap-1.5">
                                                <span>{sk.name}</span>
                                                <ArrowUpRight className="w-3 h-3 text-slate-500 group-hover:text-purple-400 transition-colors opacity-0 group-hover:opacity-100" />
                                            </div>
                                            <span className="block text-[10px] text-slate-500 font-normal mt-0.5">
                                                By {sk.author || "SecOps Team"}
                                            </span>
                                        </td>

                                        {/* Type */}
                                        <td className="py-3.5 px-3 font-mono text-[11px] text-slate-400 whitespace-nowrap">
                                            {sk.type}
                                        </td>

                                        {/* Module */}
                                        <td className="py-3.5 px-3 font-mono text-[11px] whitespace-nowrap">
                                            <span className={getModuleColor(sk.module)}>
                                                {sk.module}
                                            </span>
                                        </td>

                                        {/* Status Toggle / Draft */}
                                        <td className="py-3.5 px-3 whitespace-nowrap">
                                            {sk.status === "DRAFT" ? (
                                                <span className="px-2 py-0.5 text-[9px] font-mono font-bold bg-amber-500/10 text-amber-300 border border-amber-500/20 rounded">
                                                    DRAFT
                                                </span>
                                            ) : (
                                                <button
                                                    onClick={(e) => handleToggleSkill(sk, e)}
                                                    className={`w-8 h-4.5 rounded-full transition-colors relative p-0.5 focus:outline-none ${
                                                        sk.enabled ? "bg-purple-600" : "bg-slate-700"
                                                    }`}
                                                >
                                                    <div
                                                        className={`w-3.5 h-3.5 rounded-full bg-white transition-transform ${
                                                            sk.enabled ? "translate-x-3.5" : "translate-x-0"
                                                        }`}
                                                    />
                                                </button>
                                            )}
                                        </td>

                                        {/* Last Updated */}
                                        <td className="py-3.5 px-3 font-mono text-slate-400 text-[11px] whitespace-nowrap text-right">
                                            {sk.lastUpdated}
                                        </td>

                                        {/* Actions */}
                                        <td className="py-3.5 px-3 text-right whitespace-nowrap">
                                            <button
                                                type="button"
                                                onClick={(e) => handleDeleteSkill(e, sk.id, sk.name)}
                                                className="p-1.5 text-slate-500 hover:text-rose-400 hover:bg-rose-500/10 rounded-lg transition-colors inline-flex items-center justify-center"
                                                title="Delete Skill"
                                            >
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                </div>

            </div>

            {/* ─── Skill Inspector Drawer / Modal ─── */}
            {selectedSkill && (
                <div className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-[#0F172A] border border-white/[0.08] rounded-2xl w-full max-w-xl p-6 space-y-4 shadow-2xl animate-in zoom-in-95 duration-150 text-[12px]">
                        <div className="flex items-center justify-between border-b border-white/[0.06] pb-3">
                            <div className="space-y-0.5">
                                <span className="text-[10px] font-mono uppercase text-purple-400 bg-purple-500/10 px-2 py-0.5 rounded border border-purple-500/20">
                                    {selectedSkill.type} • {selectedSkill.module}
                                </span>
                                <h3 className="text-[16px] font-bold text-white">
                                    {selectedSkill.name}
                                </h3>
                            </div>
                            <button onClick={() => setSelectedSkill(null)} className="text-slate-400 hover:text-white">
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        <p className="text-[12px] text-slate-300">
                            {selectedSkill.description}
                        </p>

                        {/* AI Reasoning Prompt Logic */}
                        <div className="space-y-1.5">
                            <span className="text-[10.5px] font-mono text-slate-400 uppercase tracking-wider block">
                                QueryTel Prompt & Forensic Reasoning Directive:
                            </span>
                            <pre className="p-3.5 bg-black/50 border border-white/[0.06] rounded-xl text-slate-200 font-mono text-[11px] whitespace-pre-wrap leading-relaxed">
                                {selectedSkill.prompt || "No custom prompt directive configured."}
                            </pre>
                        </div>

                        <div className="flex items-center justify-between pt-3 border-t border-white/[0.06]">
                            <span className="text-[11px] font-mono text-slate-500">
                                Author: {selectedSkill.author || "SecOps Team"} • Status: {selectedSkill.status}
                            </span>
                            <button
                                onClick={() => setSelectedSkill(null)}
                                className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-bold shadow-md"
                            >
                                Done
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ─── Create Skill Modal ─── */}
            {createModalOpen && (
                <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-[#0F172A] border border-white/[0.08] rounded-2xl w-full max-w-lg p-6 space-y-4 shadow-2xl animate-in zoom-in-95 duration-150 text-[12px]">
                        <div className="flex items-center justify-between border-b border-white/[0.06] pb-3">
                            <h3 className="text-[15px] font-bold text-white">Create AI Investigation Skill</h3>
                            <button onClick={() => setCreateModalOpen(false)} className="text-slate-400 hover:text-white">
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        <form onSubmit={handleCreateSkill} className="space-y-3.5">
                            <div>
                                <label className="text-slate-400 block mb-1">Skill Name</label>
                                <input
                                    type="text"
                                    required
                                    placeholder="e.g. Kerberoasting Ticket Triage Strategy"
                                    value={newSkillForm.name}
                                    onChange={(e) => setNewSkillForm({ ...newSkillForm, name: e.target.value })}
                                    className="w-full bg-black/40 border border-white/[0.08] rounded-lg px-3 py-2 text-white outline-none focus:border-purple-500"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-slate-400 block mb-1">Type</label>
                                    <select
                                        value={newSkillForm.type}
                                        onChange={(e) => setNewSkillForm({ ...newSkillForm, type: e.target.value })}
                                        className="w-full bg-[#1E293B] border border-white/[0.12] rounded-lg px-3 py-2 text-white outline-none"
                                    >
                                        <option value="Strategy">Strategy</option>
                                        <option value="Command">Command</option>
                                        <option value="Context">Context</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="text-slate-400 block mb-1">Module Scope</label>
                                    <select
                                        value={newSkillForm.module}
                                        onChange={(e) => setNewSkillForm({ ...newSkillForm, module: e.target.value })}
                                        className="w-full bg-[#1E293B] border border-white/[0.12] rounded-lg px-3 py-2 text-white outline-none"
                                    >
                                        <option value="Investigation">Investigation</option>
                                        <option value="Threat Hunt">Threat Hunt</option>
                                        <option value="Copilot Chat">Copilot Chat</option>
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className="text-slate-400 block mb-1">Description</label>
                                <input
                                    type="text"
                                    placeholder="e.g. AI reasoning prompt to analyze Event ID 4769 SPN requests"
                                    value={newSkillForm.description}
                                    onChange={(e) => setNewSkillForm({ ...newSkillForm, description: e.target.value })}
                                    className="w-full bg-black/40 border border-white/[0.08] rounded-lg px-3 py-2 text-white outline-none"
                                />
                            </div>

                            <div>
                                <label className="text-slate-400 block mb-1">AI Reasoning Prompt Directive</label>
                                <textarea
                                    rows="3"
                                    required
                                    placeholder="When investigating Kerberoasting, check for RC4 ticket encryption type (0x17) and service accounts with elevated Active Directory domain privileges."
                                    value={newSkillForm.prompt}
                                    onChange={(e) => setNewSkillForm({ ...newSkillForm, prompt: e.target.value })}
                                    className="w-full bg-black/40 border border-white/[0.08] rounded-lg px-3 py-2 text-white outline-none font-mono text-[11px]"
                                />
                            </div>

                            <div className="flex items-center justify-end gap-2 pt-3 border-t border-white/[0.06]">
                                <button
                                    type="button"
                                    onClick={() => setCreateModalOpen(false)}
                                    className="px-4 py-2 bg-white/[0.04] hover:bg-white/[0.08] text-slate-300 rounded-lg"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg font-bold shadow-md"
                                >
                                    Create Skill
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
